// Spatial focus navigation with the remote's arrow keys. Focusable elements are [data-focus].
const DIRS: Record<number, 'left' | 'up' | 'right' | 'down'> = { 37: 'left', 38: 'up', 39: 'right', 40: 'down' };

function center(r: DOMRect) {
  return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
}

function visible(el: Element) {
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0 && getComputedStyle(el).visibility !== 'hidden';
}

function pick(from: Element, dir: string, candidates: Element[]) {
  const a = center(from.getBoundingClientRect());
  let best: Element | null = null;
  let bestScore = Infinity;
  for (const el of candidates) {
    if (el === from) continue;
    const b = center(el.getBoundingClientRect());
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const ok = (dir === 'left' && dx < -4) || (dir === 'right' && dx > 4) || (dir === 'up' && dy < -4) || (dir === 'down' && dy > 4);
    if (!ok) continue;
    const horizontal = dir === 'left' || dir === 'right';
    const score = (horizontal ? Math.abs(dx) : Math.abs(dy)) + (horizontal ? Math.abs(dy) : Math.abs(dx)) * 2.5;
    if (score < bestScore) {
      bestScore = score;
      best = el;
    }
  }
  return best;
}

/** If a full-screen layer is open, focus stays inside it. */
export function focusables(root: ParentNode = document) {
  const scopes = root.querySelectorAll('[data-focus-scope]');
  const scope = scopes.length ? scopes[scopes.length - 1] : root;
  return Array.from(scope.querySelectorAll('[data-focus]')).filter(visible);
}

/** Row the launcher is scrolling to (a smooth scroll is still moving, so scrollTop is not the truth yet). */
let launcherFirst: { row: number; at: number } | undefined;

/**
 * The launcher scrolls in whole rows: the widget that got focus is brought fully into the visible rows, so a row is never
 * left half-hidden at the bottom. Widgets taller than the visible rows (and full-screen layers) use plain scrollIntoView.
 */
function snapLauncher(el: Element) {
  const zone = el.closest('.fl-zone-launcher') as HTMLElement | null;
  if (!zone || el.closest('[data-focus-scope]')) return;
  let w: Element | null = el;
  while (w && w.parentElement !== zone) w = w.parentElement;
  if (!w) return;
  const cs = getComputedStyle(zone);
  const row = parseFloat(cs.getPropertyValue('--fl-row'));
  const visibleRows = parseInt(cs.getPropertyValue('--fl-rows'), 10);
  const padTop = parseFloat(cs.paddingTop) || 0;
  if (!(row > 0) || !(visibleRows > 0)) return;
  const zr = zone.getBoundingClientRect();
  const wr = w.getBoundingClientRect();
  const gap = parseFloat(cs.getPropertyValue('--fl-gap')) || 0;
  const top = Math.round((wr.top - zr.top + zone.scrollTop - padTop) / row);
  const rows = Math.max(1, Math.round((wr.height + gap) / row));
  if (rows > visibleRows) {
    launcherFirst = undefined;
    return;
  }
  let first = launcherFirst && Date.now() - launcherFirst.at < 700 ? launcherFirst.row : Math.round(zone.scrollTop / row); // first row shown at the top
  if (top < first) first = top;
  else if (top + rows > first + visibleRows) first = top + rows - visibleRows;
  launcherFirst = { row: first, at: Date.now() };
  const max = Math.max(0, zone.scrollHeight - zone.clientHeight);
  zone.scrollTo({ top: Math.max(0, Math.min(first * row, max)), behavior: 'smooth' });
}

export function focus(el: Element | null | undefined) {
  if (!el) return;
  (el as HTMLElement).focus({ preventScroll: true });
  // Scrolls every scroller in between (shelves, layers); the launcher's own vertical scroll is then replaced by the row snap.
  el.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
  snapLauncher(el);
}

export function focusFirst() {
  const cur = document.activeElement;
  const all = focusables();
  if (cur && all.includes(cur)) return;
  focus(all[0]);
}

export function initNav(onBack: () => void) {
  document.addEventListener('keydown', (e) => {
    const dir = DIRS[e.keyCode];
    if (dir) {
      e.preventDefault();
      const cur = document.activeElement;
      const all = focusables();
      if (!cur || !all.includes(cur)) return focus(all[0]);
      focus(pick(cur, dir, all));
    } else if (e.keyCode === 13) {
      // Prevent a double trigger with the button's own click.
      e.preventDefault();
      (document.activeElement as HTMLElement | null)?.click();
    } else if (e.keyCode === 10009 || e.keyCode === 27) {
      e.preventDefault();
      onBack();
    }
  });
}
